import { motion } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import styles from './InteractiveFlowChart.module.scss';

const flowSteps = [
  {
    id: 1,
    title: 'Confirm Your Eligibility',
    description:
      'Use our exclusive platform to quickly verify your trademarked logo and ensure eligibility.',
  },
  {
    id: 2,
    title: 'Submit Your Application',
    description:
      'Our submission system makes it easy to provide your domain, logo, and organization details.',
  },
  {
    id: 3,
    title: 'Validate Your Identity',
    description: 'Our AI-powered verification process confirms your domain, trademark, and more.',
  },
  {
    id: 4,
    title: 'Set Up Your Domain',
    description:
      'Our automated setup guide adds a DNS record with DMARC enforcement for proper logo display.',
  },
  {
    id: 5,
    title: 'Display Your Trusted Logo',
    description:
      'With continuous BIMI monitoring, your logo is guaranteed to appear, boosting email trust.',
    hasCheckmark: true,
  },
];

const InteractiveFlowChart = () => {
  return (
    <motion.div
      className={styles.flowChartContainer}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
    >
      {flowSteps.map((step, index) => (
        <React.Fragment key={step.id}>
          <FlowStep step={step} />
          {index < flowSteps.length - 1 && <DottedLine />}
        </React.Fragment>
      ))}
    </motion.div>
  );
};

const FlowStep = ({ step }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };

    checkMobile();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkMobile);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', checkMobile);
      }
    };
  }, []);

  const active = isMobile ? isOpen : isHovered;

  return (
    <div className={styles.stepContainer}>
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: step.id * 0.15 }}
      >
        <div className={styles.stepNumber}>{step.id}.</div>
      </motion.div>
      <div
        className={styles.boxContainer}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        onClick={() => isMobile && setIsOpen((prev) => !prev)}
      >
        {/* Green Box */}
        <motion.div
          className={styles.greenBox}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            delay: step.id * 0.15,
          }}
          animate={{
            border: active ? '3px solid #FFFFFF' : '3px solid transparent',
            scale: active ? 1.02 : 1,
            boxShadow: active
              ? '0px 6px 15px rgba(0, 0, 0, 0.15)'
              : '0px 4px 10px rgba(0, 0, 0, 0.1)',
            transition: { duration: 0.3 },
          }}
        >
          <div className={styles.greenBoxText}>{step.title}</div>

          {/* Checkmark for the last step */}
          {step.hasCheckmark && (
            <motion.div
              className={styles.checkmarkContainer}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                delay: 1,
              }}
              animate={{
                opacity: active ? 1 : 0.8,
                scale: active ? 1.2 : 1,
                rotate: active ? 5 : 0,
                transition: { duration: 0.3 },
              }}
            >
              <svg
                width="63"
                height="60"
                viewBox="0 0 63 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1789_488)">
                  <path
                    d="M31.5 52C43.9264 52 54 41.9264 54 29.5C54 17.0736 43.9264 7 31.5 7C19.0736 7 9 17.0736 9 29.5C9 41.9264 19.0736 52 31.5 52Z"
                    fill="white"
                  />
                  <path
                    d="M21.7749 59L16.3668 49.7143L5.62164 47.5L6.83135 37L0 29L6.83135 21.0714L5.62164 10.5714L16.3668 8.35714L21.7749 -1L31.3104 3.42857L40.8458 -1L46.3251 8.35714L56.9991 10.5714L55.7894 21.0714L62.6207 29L55.7894 37L56.9991 47.5L46.3251 49.7143L40.8458 59L31.3104 54.5714L21.7749 59ZM28.2505 38.5L44.4038 22.4286L41.2016 19.5L28.2505 32.3571L21.4903 25.2857L18.2169 28.5L28.2505 38.5Z"
                    fill="#247AFE"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1789_488">
                    <rect width="63" height="60" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </motion.div>
          )}
        </motion.div>

        {/* White Box that slides out */}
        <motion.div
          className={styles.whiteBox}
          animate={{
            x: isMobile ? 0 : active ? 290 : 0,
            y: isMobile ? (active ? 130 : 0) : 0,
            opacity: active ? 1 : 0,
            visibility: active ? 'visible' : 'hidden',
            pointerEvents: active ? 'auto' : 'none',
            transition: {
              type: 'spring',
              stiffness: 400,
              damping: 25,
              duration: 0.1,
            },
          }}
        >
          <div className={styles.whiteBoxText}>{step.description}</div>
        </motion.div>
      </div>
    </div>
  );
};

const DottedLine = () => {
  return (
    <motion.div
      className={styles.dottedLine}
      initial={{ opacity: 0, scaleY: 0 }}
      whileInView={{ opacity: 1, scaleY: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
    ></motion.div>
  );
};

export default InteractiveFlowChart;
